import { providers } from '../../providers';
import { volcengine } from '../volcengine';
import { wavespeed } from '../wavespeed';
import { getUiOptionsForModel } from '../ui-options';
import type { VideoModelEntry } from '../types';

// 价格表: 人民币/秒
const SD15P_480P = (854 * 480 * 24 / 1024 / 1000000 * 8);
const SD15P_480P_AUDIO = (854 * 480 * 24 / 1024 / 1000000 * 16);
const SD15P_720P = (1280 * 720 * 24 / 1024 / 1000000 * 8);
const SD15P_720P_AUDIO = (1280 * 720 * 24 / 1024 / 1000000 * 16);
const SD15P_1080P = (1920 * 1080 * 24 / 1024 / 1000000 * 8);
const SD15P_1080P_AUDIO = (1920 * 1080 * 24 / 1024 / 1000000 * 16);
const SD10P_480P = (854 * 480 * 24 / 1024 / 1000000 * 15);
const SD10P_720P = (1280 * 720 * 24 / 1024 / 1000000 * 15);
const SD10P_1080P = (1920 * 1080 * 24 / 1024 / 1000000 * 15);
const SD10P_480PF = (854 * 480 * 24 / 1024 / 1000000 * 4.2);
const SD10P_720PF = (1280 * 720 * 24 / 1024 / 1000000 * 4.2);
const SD10P_1080PF = (1920 * 1080 * 24 / 1024 / 1000000 * 4.2);

// 系数
const COST_FACTOR = 1.5;
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// usd to rmb 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;

export const bytedanceModels: VideoModelEntry[] = [
    {
        id: 'seedance-1.5-pro',
        chef: providers.bytedance,
        providers: [
            {
                ...providers.bytedance,
                model: volcengine('seedance-1.5-pro'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const audio = providerOptions?.audio === true;
                    if (quality === '480p') return usd(duration * (audio ? SD15P_480P_AUDIO : SD15P_480P)) * COST_FACTOR;
                    if (quality === '720p') return usd(duration * (audio ? SD15P_720P_AUDIO : SD15P_720P)) * COST_FACTOR;
                    if (quality === '1080p') return usd(duration * (audio ? SD15P_1080P_AUDIO : SD15P_1080P)) * COST_FACTOR;
                    return usd(duration * (audio ? SD15P_1080P_AUDIO : SD15P_1080P)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const audio = providerOptions?.audio === true;
                    if (quality === '480p') return usd(duration * (audio ? SD15P_480P_AUDIO : SD15P_480P)) * COST_FACTOR * 100;
                    if (quality === '720p') return usd(duration * (audio ? SD15P_720P_AUDIO : SD15P_720P)) * COST_FACTOR * 100;
                    if (quality === '1080p') return usd(duration * (audio ? SD15P_1080P_AUDIO : SD15P_1080P)) * COST_FACTOR * 100;
                    return usd(duration * (audio ? SD15P_1080P_AUDIO : SD15P_1080P)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v','first','first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-1.5-pro'),
    },
    {
        id: 'seedance-1.0-pro',
        chef: providers.bytedance,
        providers: [
            {
                ...providers.bytedance,
                model: volcengine('seedance-1.0-pro'),
                getCost: ({ duration, quality }) => {
                    if (quality === '480p') return usd(duration * SD10P_480P) * COST_FACTOR;
                    if (quality === '720p') return usd(duration * SD10P_720P) * COST_FACTOR;
                    if (quality === '1080p') return usd(duration * SD10P_1080P) * COST_FACTOR;
                    return usd(duration * SD10P_1080P) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality }) => {
                    if (quality === '480p') return usd(duration * SD10P_480P) * COST_FACTOR * 100;
                    if (quality === '720p') return usd(duration * SD10P_720P) * COST_FACTOR * 100;
                    if (quality === '1080p') return usd(duration * SD10P_1080P) * COST_FACTOR * 100;
                    return usd(duration * SD10P_1080P) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-1.0-pro'),
    },
    {
        id: 'seedance-1.0-pro-fast',
        chef: providers.bytedance,
        providers: [
            {
                ...providers.bytedance,
                model: volcengine('seedance-1.0-pro-fast'),
                getCost: ({ duration, quality }) => {
                    if (quality === '480p') return usd(duration * SD10P_480PF) * COST_FACTOR;
                    if (quality === '720p') return usd(duration * SD10P_720PF) * COST_FACTOR;
                    if (quality === '1080p') return usd(duration * SD10P_1080PF) * COST_FACTOR;
                    return usd(duration * SD10P_1080PF);
                },
                getPassCount: ({ duration, quality }) => {
                    if (quality === '480p') return usd(duration * SD10P_480PF) * COST_FACTOR * 100;
                    if (quality === '720p') return usd(duration * SD10P_720PF) * COST_FACTOR * 100;
                    if (quality === '1080p') return usd(duration * SD10P_1080PF) * COST_FACTOR * 100;
                    return usd(duration * SD10P_1080PF) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-1.0-pro-fast'),
    },
    {
        id: 'avatar-omni-human-1.5',
        chef: providers.bytedance,
        hideInstructions: true,
        providers: [
            {
                ...providers.bytedance,
                model: wavespeed('avatar-omni-human-1.5'),
                getCost: ({ providerOptions }) => {
                    const inputDur = providerOptions?._media?.audioDurationSeconds ?? 15;
                    return inputDur * 0.25 * COST_FACTOR;
                },
                getPassCount: ({ providerOptions }) => {
                    const inputDur = providerOptions?._media?.audioDurationSeconds ?? 15;
                    return inputDur * 0.25 * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            avatar: {
                aspectRatios: [],
                durations: [],
                qualities: [],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '10MB', slot: true, formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false, maxCount: 0, maxSize: '0' },
                    audio: { allow: true, maxCount: 1, maxSize: '10MB', slot: true, formats: ['audio/mpeg', 'audio/wav'] },
                    maxTotalCount: 2,
                },
            },
        },
        precheckByMode: {
            avatar: ['image', 'audio'],
        },
        supportsEdit: false,
        supportsInput: ['avatar'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];